import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../../../Components/Loader/Button";
import SVG from "../../../Components/SVG";
import { API_BASE_URL } from "../../../config/api";

export default function ResetAccountModal({
  account,
  onClose,
  onSuccessClose,
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState("");

  const closeModal = () => {
    if (submitLoading) return;
    if (step === 2) {
      onSuccessClose();
      return;
    }
    onClose();
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "Escape" || submitLoading) return;
      if (step === 2) {
        onSuccessClose();
        return;
      }
      onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onSuccessClose, step, submitLoading]);

  const logout = () => {
    localStorage.removeItem("access");
    localStorage.removeItem("authCredentials");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    navigate("/signin");
  };

  const handleSubmit = async () => {
    setSubmitLoading(true);
    setError("");

    try {
      let response = await fetch(
        `${API_BASE_URL}challenges/trader/accounts/${account.id}/reset/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        },
      );

      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}auth/trader/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          logout();
          return;
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);
        response = await fetch(
          `${API_BASE_URL}challenges/trader/accounts/${account.id}/reset/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${refreshData.access}`,
            },
          },
        );
      }

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      setStep(2);
    } catch (requestError) {
      console.error("Error resetting account:", requestError);
      setError("فعال کردن حساب با خطا مواجه شد، مجددا تلاش کنید.");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm"
      onClick={closeModal}
    >
      {step === 1 ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="reset-account-confirm-title"
          className="w-full max-w-fit rounded-2xl bg-zi-bg2-light p-5 text-right shadow-2xl drop-shadow-lg dark:bg-zi-bg2-dark"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex flex-col gap-3">
            <h5
              id="reset-account-confirm-title"
              className="text-base font-medium text-zi-text-light dark:text-zi-text-dark sm:text-lg"
            >
              آیا از فعال‌سازی مجدد این حساب مطمئن هستید؟
            </h5>
            <p className="text-sm leading-6 text-zi-text-light/70 dark:text-zi-text-dark/70">
              با فعال کردن مجدد حساب، چالش شما از ابتدا شروع خواهد شد.
            </p>
            <p className="text-sm leading-6 text-zi-text-light/70 dark:text-zi-text-dark/70">
              توجه: تنها یک‌بار مجاز به فعال‌سازی مجدد این حساب خود هستید و این
              امکان صرفا برای فاز اول قابل استفاده است.
            </p>
          </div>

          {error && (
            <p className="mt-4 text-sm text-red-500">
              فعال کردن حساب با خطا مواجه شد، مجددا تلاش کنید.
            </p>
          )}

          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              type="button"
              disabled={submitLoading}
              className="rounded-xl border border-zi-border-light bg-zi-bg1-light px-4 py-2.5 text-sm text-zi-text-light outline-none hocus:ring-1 hocus:ring-zi-border-dark disabled:cursor-not-allowed disabled:opacity-60 dark:border-zi-border-dark dark:bg-zi-bg1-dark dark:text-zi-text-dark"
              onClick={onClose}
            >
              انصراف
            </button>
            <button
              type="button"
              disabled={submitLoading}
              className="flex items-center justify-center rounded-xl bg-zi-gradient-button-yellow px-4 py-2.5 text-sm text-zi-text-dark outline-none hocus:ring-1 hocus:ring-offset-1 hocus:ring-zi-border-dark disabled:cursor-not-allowed"
              onClick={handleSubmit}
            >
              {submitLoading ? <Loader /> : "فعال کردن"}
            </button>
          </div>
        </div>
      ) : (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="reset-account-success-title"
          className="p-5 w-full max-w-2xl min-h-96 rounded-2xl flex flex-col gap-6 justify-between bg-zi-bg2-light dark:bg-zi-bg2-dark drop-shadow-lg"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="w-full flex items-start justify-between gap-3">
            <h5
              id="reset-account-success-title"
              className="font-medium text-base sm:text-xl text-zi-text-light dark:text-zi-text-dark overflow-hidden whitespace-nowrap text-ellipsis"
            >
              فعال‌سازی مجدد حساب
            </h5>
            <button type="button" onClick={onSuccessClose}>
              <SVG
                name="close"
                classname="stroke-zi-text-light dark:stroke-zi-text-dark size-5 cursor-pointer"
              />
            </button>
          </div>

          <div className="flex flex-col gap-4 items-center justify-center font-normal text-center text-sm sm:text-base text-zi-text-light/70 dark:text-zi-text-dark/70">
            <SVG
              name="check"
              classname="size-24 stroke-2 stroke-zi-text-light dark:stroke-zi-text-dark"
            />
            <p className="text-zi-text-light dark:text-zi-text-dark">
              حساب شما با موفقیت فعال شد.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <p className="font-light text-sm sm:text-base text-zi-text-light/70 dark:text-zi-text-dark/70">
              جهت مشاهده حساب فعال خود بر روی دکمه زیر بزنید.
            </p>
            <button
              type="button"
              onClick={onSuccessClose}
              className="bg-zi-gradient-button-blue rounded-xl py-3 text-base text-zi-text-dark w-full outline-none hocus:ring-1 hocus:ring-offset-1 hocus:ring-zi-border-dark"
            >
              مشاهده لیست حساب‌ها و چالش‌ها
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
